// Google Analytics 4 helper
// GA4 base tag is loaded in index.html. Enhanced Measurement handles page_view
// (including SPA history changes) automatically.

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export const GA_MEASUREMENT_ID = 'G-CTKJZE8FED';

export const trackEvent = (
  eventName: string,
  params: Record<string, unknown> = {},
): void => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, params);
};

/** Best-effort partner name from an affiliate URL hostname. */
export const inferAffiliatePartner = (url?: string | null): string => {
  if (!url) return 'unknown';
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    return host.split('.')[0] || host;
  } catch {
    return 'unknown';
  }
};

/** Traffic source from document.referrer (empty when direct/same-origin). */
export const currentTrafficSource = (): string => {
  if (typeof document === 'undefined') return 'direct';
  const ref = document.referrer;
  if (!ref) return 'direct';
  try {
    const host = new URL(ref).hostname;
    if (typeof window !== 'undefined' && host === window.location.hostname) return 'internal';
    return host;
  } catch {
    return 'direct';
  }
};

/* --------------------------------------------------------------------------
 * Planner abandonment tracking
 *
 * Fires `planner_abandoned` exactly once per planner session when a user who
 * started the planner leaves it without completing. Triggers:
 *   - component unmount (route change / back navigation)
 *   - tab hide / pagehide (sendBeacon-friendly via gtag transport)
 *   - 60s of inactivity (no pointer/key/touch input)
 * Completion clears the session so nothing fires afterwards.
 * ------------------------------------------------------------------------ */

interface PlannerSession {
  startedAt: number;
  lastActivity: number;
  getCurrentStep: () => number;
  fired: boolean;
  completed: boolean;
  cleanup: () => void;
}

let plannerSession: PlannerSession | null = null;

/** True while a planner session is currently open (started, not yet
 * completed or fired as abandoned). Lets a caller like the trip planner's
 * "Adjust Preferences" handler decide whether it needs to arm a fresh
 * session -- e.g. after a completion (which nulls the session below via
 * completePlannerSession), or after resuming a handed-off already-completed
 * session (which deliberately never arms one on mount) -- versus leaving an
 * already-open session alone, e.g. when adjusting preferences after an RPC
 * error, where the original session never closed and re-arming would fire a
 * spurious extra abandonment for it. */
export const hasActivePlannerSession = (): boolean => plannerSession !== null;

const INACTIVITY_MS = 60_000;

export const beginPlannerSession = (getCurrentStep: () => number): void => {
  // If a previous unfinished session exists, treat it as abandoned before
  // starting a new one so we never lose the signal.
  if (plannerSession && !plannerSession.completed && !plannerSession.fired) {
    firePlannerAbandoned('restart');
  }
  endPlannerSession(false);

  if (typeof window === 'undefined') return;

  const now = Date.now();
  const session: PlannerSession = {
    startedAt: now,
    lastActivity: now,
    getCurrentStep,
    fired: false,
    completed: false,
    cleanup: () => {},
  };

  const bumpActivity = () => {
    session.lastActivity = Date.now();
  };
  const activityEvents: Array<keyof WindowEventMap> = [
    'pointerdown',
    'keydown',
    'touchstart',
    'scroll',
  ];
  activityEvents.forEach((evt) =>
    window.addEventListener(evt, bumpActivity, { passive: true }),
  );

  const inactivityTimer = window.setInterval(() => {
    if (Date.now() - session.lastActivity >= INACTIVITY_MS) {
      firePlannerAbandoned('inactivity');
    }
  }, 5_000);

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') firePlannerAbandoned('hidden');
  };
  const onPageHide = () => firePlannerAbandoned('pagehide');

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);

  session.cleanup = () => {
    activityEvents.forEach((evt) => window.removeEventListener(evt, bumpActivity));
    window.clearInterval(inactivityTimer);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
  };

  plannerSession = session;
};

const firePlannerAbandoned = (reason: string): void => {
  const session = plannerSession;
  if (!session || session.fired || session.completed) return;
  session.fired = true;
  const seconds = Math.round((Date.now() - session.startedAt) / 1000);
  trackEvent('planner_abandoned', {
    current_step: session.getCurrentStep(),
    time_spent_seconds: seconds,
    reason,
  });
  session.cleanup();
};

/** Mark the planner as completed — prevents any future abandonment fire. */
export const completePlannerSession = (): void => {
  if (!plannerSession) return;
  plannerSession.completed = true;
  plannerSession.cleanup();
  plannerSession = null;
};

/** Called on unmount. If not completed, fire abandonment. */
export const endPlannerSession = (fireOnEnd = true): void => {
  const session = plannerSession;
  if (!session) return;
  if (fireOnEnd && !session.completed && !session.fired) {
    firePlannerAbandoned('unmount');
  }
  session.cleanup();
  plannerSession = null;
};

